import { useState, useEffect, useRef, useCallback } from 'react';
import { DateRange, type Range, type RangeKeyDict } from 'react-date-range';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import type { DateRangeValue } from 'types';
import {
  DateRangePickerContainer,
  DateRangePickerLabel,
  DateRangeInput,
  CalendarPopup,
  Overlay,
} from './CustomDateRangePicker.styled';

interface Props {
  label?: string;
  placeholder?: string;
  value?: DateRangeValue;
  onChange?: (value: DateRangeValue) => void;
}

const CALENDAR_HEIGHT = 330;

const CustomDateRangePicker = ({ label, placeholder = 'Seleccionar rango', value, onChange }: Props) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  const calcPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= CALENDAR_HEIGHT ? rect.bottom + 4 : rect.top - CALENDAR_HEIGHT - 4;
    setPopupPos({ top, left: rect.left });
  }, []);
  const [ranges, setRanges] = useState<Range[]>([
    {
      startDate: value?.startDate || new Date(),
      endDate: value?.endDate || new Date(),
      key: 'selection',
    },
  ]);

  // Sincronizar estado interno cuando el valor externo cambia (ej: reset)
  useEffect(() => {
    setRanges([
      {
        startDate: value?.startDate || new Date(),
        endDate: value?.endDate || new Date(),
        key: 'selection',
      },
    ]);
  }, [value?.startDate, value?.endDate]);

  const handleSelect = (item: RangeKeyDict) => {
    const selection = item.selection;
    setRanges([selection]);
    onChange?.({
      startDate: selection.startDate,
      endDate: selection.endDate,
    });
  };

  const formatDateRange = () => {
    if (!value?.startDate || !value?.endDate) return null;
    const start = format(value.startDate, 'dd/MM/yyyy');
    const end = format(value.endDate, 'dd/MM/yyyy');
    return `${start} - ${end}`;
  };

  const displayValue = formatDateRange();

  return (
    <DateRangePickerContainer>
      {label && <DateRangePickerLabel>{label}</DateRangePickerLabel>}
      <DateRangeInput
        ref={inputRef}
        hasValue={!!displayValue}
        onClick={() => {
          calcPosition();
          setIsOpen(true);
        }}
      >
        {displayValue || placeholder}
      </DateRangeInput>

      {isOpen && (
        <>
          <Overlay onClick={() => setIsOpen(false)} />
          <CalendarPopup top={popupPos.top} left={popupPos.left}>
            <DateRange ranges={ranges} onChange={handleSelect} locale={es} months={1} showDateDisplay={false} />
          </CalendarPopup>
        </>
      )}
    </DateRangePickerContainer>
  );
};

export default CustomDateRangePicker;
